import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AppFooter } from '@/components/layout/AppFooter';
import { BrandLogoDark } from '@/components/BrandLogo';
import { Seo } from '@/components/Seo';
import { CountUp } from '@/components/CountUp';
import { Reveal, useInView } from '@/components/Reveal';
import { Tilt } from '@/components/Tilt';
import { TryItDemo } from '@/components/TryItDemo';
import {
  CheckCircle2, FileText, Mail, BarChart3, Users, Shield,
  ArrowRight, ChevronRight, Building2, Zap, X, Infinity,
  Brain, Upload, Link2, Layers, Lock, Clock, Eye,
  RefreshCw, CreditCard, HelpCircle, Sparkles, DollarSign,
  ShieldCheck, KeyRound, Fingerprint, Database, Archive
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'Dashboard', href: '#dashboard' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'FAQ', href: '#faq' },
];

const FEATURES = [
  { icon: Brain, title: 'AI Document Extraction', desc: 'Upload a PDF or photo. Our AI reads COIs, W-9s, and licenses like a trained compliance specialist — extracting policy numbers, coverage limits, and expiration dates in seconds.' },
  { icon: Shield, title: 'Compliance Scoring', desc: 'Every vendor gets a real-time 0–100 compliance score based on your configurable rules. Green means ready to work, yellow means attention needed, red means high risk.' },
  { icon: Mail, title: 'Auto-Chase Engine', desc: 'Set it and forget it. The system automatically sends escalating email reminders for missing or expired documents — and stops when the vendor responds.' },
  { icon: Link2, title: 'Zero-Login Vendor Portal', desc: 'Vendors receive a magic link — no account needed. They see exactly what\'s required, upload documents, and track their compliance status.' },
  { icon: Layers, title: 'Three-Layer Requirements', desc: 'Define compliance rules at three levels: vendor type defaults, property-specific overrides, and vendor-specific exceptions. The system merges them automatically.' },
  { icon: Building2, title: 'Multi-Property Management', desc: 'Manage compliance across your entire portfolio. Each property can have its own insurance requirements, coverage minimums, and endorsement rules.' },
  { icon: BarChart3, title: 'Reporting & Analytics', desc: 'Portfolio compliance dashboards, vendor-level reports, CSV exports, and predictive renewal insights. Owner-ready compliance summaries at a click.' },
  { icon: Upload, title: 'Bulk CSV Import', desc: 'Import hundreds of vendors or properties from a CSV file. Smart column mapping, row-level validation, and partial import support.' },
  { icon: Zap, title: 'Auto-Approve', desc: 'Optionally auto-approve AI extractions that meet your criteria — by vendor type, confidence threshold, or document type. Always disabled by default.' },
  { icon: Eye, title: 'Coverage Comparison', desc: 'Instantly compare vendor coverage against property-specific requirements. See exact gaps, percentage shortfalls, and weighted scores per property.' },
  { icon: Clock, title: 'Predictive Renewals', desc: 'The system learns each vendor\'s renewal behavior and triggers early chasers based on historical patterns — before documents expire.' },
  { icon: Lock, title: 'RBAC & Security', desc: 'Role-based access control (Admin, Manager, Assistant, Owner). JWT authentication, multi-tenant isolation, and SSL encryption.' },
];

const STEPS = [
  { num: 1, title: 'Add Properties & Vendors', desc: 'Import your portfolio via CSV or add manually. Set insurance requirements per property.', icon: Building2 },
  { num: 2, title: 'Send Magic Links', desc: 'Generate zero-login portal links for each vendor. They see exactly what\'s needed.', icon: Link2 },
  { num: 3, title: 'AI Processes Documents', desc: 'Vendors upload COIs and W-9s. Our AI extracts fields and flags issues instantly.', icon: Brain },
  { num: 4, title: 'Review & Approve', desc: 'Review AI-extracted data in the split-screen viewer. Approve or edit before it counts.', icon: CheckCircle2 },
  { num: 5, title: 'Auto-Chase Handles the Rest', desc: 'Non-compliant vendors get automated reminders. You get dashboard alerts and reports.', icon: Mail },
];

const FAQS = [
  { q: 'How does the AI extraction work?', a: 'We use a two-stage pipeline: automated OCR reads the document, then an AI model performs intelligent field extraction—understanding what it reads. It identifies policy numbers, coverage amounts, expiration dates, carrier names, and more in seconds, then presents everything for optional review before it affects compliance scores.' },
  { q: 'Is there a human review step?', a: 'Yes. Every AI extraction lands in "Pending Review" status. A human must click "Approve" in the document viewer before the data affects vendor compliance scores. This Human-in-the-Loop design is a core safety feature. You can optionally enable Auto-Approve for trusted scenarios.' },
  { q: 'What document types are supported?', a: 'Certificates of Insurance (COI), W-9 tax forms, trade licenses, workers compensation certificates, auto insurance, umbrella policies, surety bonds, safety documents, and service contracts. The AI auto-detects the document type.' },
  { q: 'How is an "active" vendor defined for billing?', a: 'An active vendor is one that has at least one property assignment OR has been updated in the last 90 days. Vendors without any property assignments are NOT counted toward your monthly bill. This ensures you only pay for vendors actively working on your properties. Note: Vendor-Paid vendors (Enterprise only, where the vendor pays their own $50/year fee) are never counted toward PM billing, regardless of assignment status.' },
  { q: 'How does the vendor portal work?', a: 'You generate a magic link for each vendor. They click it — no account, no password, no login. They see a checklist of required documents, upload files via drag-and-drop, and the AI processes them immediately. Links are valid for 30 days and can be refreshed anytime.' },
  { q: 'Can different properties have different requirements?', a: 'Absolutely. VendorSuite uses a three-layer compliance system: vendor type defaults → property-level overrides → vendor-specific exceptions. A commercial high-rise can require $2M liability while a residential complex requires $1M — and a sole proprietor can be exempted from workers comp.' },
  { q: 'What happens when a document expires?', a: 'The system automatically detects expiring documents (configurable window, default 30 days), flags them on the dashboard, and triggers the auto-chase engine to send escalating reminders to the vendor. The predictive engine can start chasing even earlier based on the vendor\'s historical renewal behavior.' },
  { q: 'What\'s the difference between Professional and Enterprise?', a: 'Both plans include every feature. The only differences: Professional supports up to 5 properties at $4.50/vendor/month. Enterprise offers unlimited properties, a lower $3.50/vendor rate, and the option to shift the compliance fee to vendors ($50/year per vendor instead of charging you).' },
  { q: 'Is my data secure?', a: 'Yes. We use SSL/TLS encryption, role-based access control, JWT authentication, multi-tenant data isolation, and SHA-256 hashed magic link tokens. Documents are stored on AWS S3. Data is retained for the subscription period plus 7 years for audit compliance.' },
  { q: 'Can I import existing vendor data?', a: 'Yes. The bulk import engine accepts CSV files for vendors, properties, and vendor-property assignments. It auto-detects column headers, validates data (checking for duplicates, invalid emails, missing fields), and supports partial imports with downloadable error reports.' },
];

const sharedFeatures = [
  'AI-powered document extraction (OCR + field understanding)',
  'Compliance scoring engine',
  'Automated vendor chasing',
  'Zero-login vendor portal',
  'Three-layer requirements system',
  'Coverage comparison & gap analysis',
  'Predictive renewal logic',
  'Auto-approve with configurable rules',
  'Bulk CSV import engine',
  'Support ticketing system',
  'Role-based access control',
  'Portfolio & vendor reporting',
];

const TREND = [35, 42, 45, 50, 55, 53, 58, 62, 65, 68, 72, 78];
const RING_ARC = 'M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831';

const BENEFITS = [
  { icon: Clock, num: 20, suffix: '+', unit: 'hrs / week', label: 'saved on admin', desc: 'Eliminate manual document review and vendor follow-ups' },
  { icon: DollarSign, num: 70, suffix: '%', unit: 'lower', label: 'compliance cost', desc: 'Reduce compliance staff overhead and late-renewal penalties' },
  { icon: Zap, num: 5, suffix: ' min', unit: 'setup', label: 'to go live', desc: 'Import vendors via CSV and start managing compliance instantly' },
  { icon: Shield, text: 'Audit Ready', unit: 'always', label: 'every vendor, every doc', desc: 'AI-powered extraction with human-in-the-loop verification' },
];

const SECURITY = [
  { icon: Lock, title: 'SSL/TLS Encryption', desc: 'All traffic is encrypted in transit with modern TLS.' },
  { icon: ShieldCheck, title: 'Role-Based Access Control', desc: 'Admin, Manager, Assistant and Owner roles scope exactly what each user can see and do.' },
  { icon: KeyRound, title: 'JWT Authentication', desc: 'Stateless, signed session tokens protect every authenticated request.' },
  { icon: Layers, title: 'Multi-Tenant Isolation', desc: "Every record is scoped to your company — your data is never mixed with another tenant's." },
  { icon: Fingerprint, title: 'SHA-256 Magic-Link Tokens', desc: 'Vendor portal links use hashed, expiring tokens — no vendor passwords to leak.' },
  { icon: Database, title: 'AWS S3 Document Storage', desc: 'Documents are stored on Amazon S3 with durable, access-controlled infrastructure.' },
  { icon: Archive, title: '7-Year Retention', desc: 'Compliance records are retained for the subscription period plus 7 years for audits.' },
  { icon: Eye, title: 'Human-in-the-Loop Review', desc: 'AI extractions require approval before affecting compliance — automation stays under your control.' },
];

const AFTER_SIGNUP = [
  { icon: Upload, title: 'Add your vendors', desc: 'Import your list via CSV or add vendors manually in seconds, and set insurance requirements per property.' },
  { icon: Link2, title: 'Send magic links', desc: 'Each vendor gets a passwordless link to upload their COI, W-9 and licenses — no account required.' },
  { icon: BarChart3, title: 'Watch scores populate', desc: 'AI reads every document and your compliance dashboard fills in real time. Auto-chase handles the rest.' },
];

const REASSURANCE = ['No credit card', 'Cancel in one click', 'Export your data anytime', 'No sales call'];

// Trend bars that grow from the baseline when scrolled into view.
const TrendBars = () => {
  const [ref, inView] = useInView(0.35);
  return (
    <div ref={ref} className="h-24 flex items-end gap-1">
      {TREND.map((h, i) => (
        <div
          key={i}
          className="flex-1 rounded-t bg-gradient-to-t from-primary/50 to-primary/15"
          style={{
            height: inView ? `${h}%` : '0%',
            transition: `height 0.9s cubic-bezier(0.22,1,0.36,1) ${i * 55}ms`,
          }}
        />
      ))}
    </div>
  );
};

// Portfolio score ring that draws itself + counts up when scrolled into view.
const ScoreRing = () => {
  const [ref, inView] = useInView(0.4);
  const target = 28;
  return (
    <div ref={ref} className="relative h-16 w-16">
      <svg viewBox="0 0 36 36" className="h-16 w-16">
        <path d={RING_ARC} fill="none" stroke="hsl(var(--muted))" strokeWidth="3" />
        <path
          d={RING_ARC}
          fill="none"
          stroke="hsl(var(--primary))"
          strokeWidth="3"
          strokeLinecap="round"
          strokeDasharray="100"
          strokeDashoffset={inView ? 100 - target : 100}
          style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.22,1,0.36,1)' }}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-lg font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
        <CountUp end={target} />
      </span>
    </div>
  );
};

export default function LandingPage() {
  const navigate = useNavigate();
  const glowFarRef = useRef(null);
  const glowNearRef = useRef(null);

  // Subtle parallax: hero glow layers drift slower than the page as you scroll.
  useEffect(() => {
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let raf = 0;
    const update = () => {
      const y = window.scrollY || 0;
      if (glowFarRef.current) glowFarRef.current.style.transform = `translate3d(0, ${y * 0.28}px, 0)`;
      if (glowNearRef.current) glowNearRef.current.style.transform = `translate3d(0, ${y * 0.12}px, 0)`;
      raf = 0;
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Force dark mode on landing page for consistent branding
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.classList.contains('dark') ? 'dark' : 'light';
    root.classList.remove('light');
    root.classList.add('dark');
    return () => {
      root.classList.remove('dark');
      root.classList.add(prev);
    };
  }, []);

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const [mobileNav, setMobileNav] = useState(false);

  const landingJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'SoftwareApplication',
        name: 'VendorSuite',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        description: 'AI-powered vendor compliance automation for property managers—track COIs, W-9s and licenses, auto-chase expiring documents, and score compliance across your portfolio.',
        url: 'https://vendorsuite.io/',
        offers: {
          '@type': 'Offer',
          price: '249',
          priceCurrency: 'USD',
          description: 'Professional plan from $249/month + $4.50 per vendor. 30-day free trial.',
        },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  };

  return (
    <div data-testid="landing-page" className="min-h-screen bg-background">
      <Seo path="/" jsonLd={landingJsonLd} />
      {/* Sticky Nav */}
      <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex h-16 items-center justify-between">
          <button type="button" onClick={scrollToTop} data-testid="landing-logo-button" aria-label="Back to top" className="shrink-0 cursor-pointer bg-transparent border-0 p-0">
            <BrandLogoDark width={140} className="shrink-0" />
          </button>
          <div className="hidden md:flex items-center gap-6">
            {NAV_ITEMS.map(item => (
              <button key={item.href} onClick={() => scrollTo(item.href)} className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">{item.label}</button>
            ))}
            <button onClick={() => navigate('/guides')} data-testid="landing-guides-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">Guides</button>
            <button onClick={() => navigate('/tools/coi-requirement-builder')} data-testid="landing-tools-link" className="text-sm text-muted-foreground hover:text-foreground transition-colors duration-150">Free Tools</button>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Button variant="ghost" size="sm" data-testid="landing-login-button" onClick={() => navigate('/login')} className="hidden sm:inline-flex">Log in</Button>
            <Button size="sm" data-testid="landing-get-started-button" onClick={() => navigate('/signup')}>Get Started <ArrowRight className="ml-1 h-3 w-3 hidden sm:inline" /></Button>
            <Button variant="ghost" size="icon" className="md:hidden h-9 w-9" data-testid="landing-mobile-nav-toggle" onClick={() => setMobileNav(!mobileNav)}>
              <span className="text-lg">&#9776;</span>
            </Button>
          </div>
        </div>
        {/* Mobile nav dropdown */}
        {mobileNav && (
          <div className="md:hidden border-t border-border bg-background px-4 py-3 space-y-1">
            {NAV_ITEMS.map(item => (
              <button key={item.href} onClick={() => { scrollTo(item.href); setMobileNav(false); }} className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-2 px-3 rounded-lg hover:bg-muted/50">{item.label}</button>
            ))}
            <button onClick={() => { navigate('/guides'); setMobileNav(false); }} data-testid="landing-guides-link-mobile" className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-2 px-3 rounded-lg hover:bg-muted/50">Guides</button>
            <button onClick={() => { navigate('/tools/coi-requirement-builder'); setMobileNav(false); }} data-testid="landing-tools-link-mobile" className="block w-full text-left text-sm text-muted-foreground hover:text-foreground py-2 px-3 rounded-lg hover:bg-muted/50">Free Tools</button>
            <Button variant="ghost" size="sm" className="w-full justify-start sm:hidden" onClick={() => navigate('/login')}>Log in</Button>
          </div>
        )}
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative overflow-hidden py-14 lg:py-28 px-4 sm:px-6">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div ref={glowFarRef} className="absolute inset-0 will-change-transform">
            <div className="absolute -top-40 left-1/4 h-[460px] w-[460px] -translate-x-1/2 rounded-full bg-primary/20 blur-[120px] landing-float" />
          </div>
          <div ref={glowNearRef} className="absolute inset-0 will-change-transform">
            <div className="absolute -bottom-40 right-1/4 h-[420px] w-[420px] translate-x-1/2 rounded-full bg-cyan-500/15 blur-[130px] landing-float-slow" />
          </div>
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <Badge variant="secondary" className="mb-4 sm:mb-6 text-xs">AI-Powered Compliance Automation</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-6xl font-bold leading-tight tracking-tight mb-4 sm:mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Vendor compliance,<br />
            <span className="text-primary">fully automated.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
            Stop chasing paperwork. VendorSuite handles document ingestion,{' '}
            <br className="hidden sm:block" />
            AI extraction, compliance scoring, and vendor follow-ups{' '}
            <br className="hidden sm:block" />
            so you can focus on managing properties.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" data-testid="hero-get-started-button" onClick={() => navigate('/signup')}>
              Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => scrollTo('#dashboard')}>
              See the Dashboard
            </Button>
          </div>

          <div data-testid="hero-risk-reversal" className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {REASSURANCE.map((r) => (
              <span key={r} className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {r}</span>
            ))}
          </div>

          {/* Animated speed stat — carries the "seconds, not days" message to the hero */}
          <div data-testid="hero-speed-stat" className="mt-12 flex flex-col items-center gap-5">
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-5 py-2.5">
              <Zap className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">
                Vendors cleared in <span className="text-primary font-semibold">seconds</span>, not{' '}
                <span className="line-through text-muted-foreground/70">days</span>
              </span>
            </div>
            <div className="flex items-stretch justify-center divide-x divide-border rounded-2xl border bg-card/40 backdrop-blur">
              <div className="px-5 sm:px-8 py-4 text-center">
                <div
                  className="text-3xl sm:text-4xl font-bold text-primary tabular-nums"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  <CountUp end={12} suffix="s" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">to read &amp; score a doc</p>
              </div>
              <div className="px-5 sm:px-8 py-4 text-center">
                <div
                  className="text-3xl sm:text-4xl font-bold tabular-nums"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  <CountUp end={90} suffix="%" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">less manual busywork</p>
              </div>
              <div className="px-5 sm:px-8 py-4 text-center">
                <div
                  className="text-3xl sm:text-4xl font-bold tabular-nums"
                  style={{ fontFamily: 'Space Grotesk, sans-serif' }}
                >
                  <CountUp end={1000} suffix="+" />
                </div>
                <p className="text-xs text-muted-foreground mt-1">vendors, zero extra staff</p>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-6 mt-8">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-2">Free Limited Demo Includes:</p>
              <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 30-day trial</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 5 vendors with docs</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> 5 properties</span>
                <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-primary" /> No credit card</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── DEMO VIDEO ─── */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">See It In Action</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Watch the full platform walkthrough</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              See how property managers automate vendor compliance—from document upload to AI extraction to automated chasing.
            </p>
          </div>
          
          <Card className="rounded-2xl border-2 shadow-[var(--shadow-pop)] overflow-hidden bg-card/50 backdrop-blur">
            <CardContent className="p-0">
              <div className="relative aspect-video bg-muted">
                <video
                  className="w-full h-full"
                  controls
                  preload="metadata"
                  poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1920 1080'%3E%3Crect fill='%23111827' width='1920' height='1080'/%3E%3Ctext x='50%25' y='50%25' font-family='system-ui' font-size='48' fill='%2328aab8' text-anchor='middle' dominant-baseline='middle'%3EClick to Play Demo%3C/text%3E%3C/svg%3E"
                  data-testid="demo-video"
                >
                  <source src="https://customer-assets.emergentagent.com/job_property-vendor-hub/artifacts/9xycgyxb_VendorSuite%20Demo%20Final%20V2.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
                {/* Play button overlay (hidden when video plays) */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 hover:opacity-100 transition-opacity duration-300">
                  <div className="h-16 w-16 rounded-full bg-primary/90 backdrop-blur flex items-center justify-center shadow-lg">
                    <svg className="h-8 w-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Key highlights below video — headline results */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mt-10">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 80} className="h-full">
                <Tilt max={7} glare={false} className="relative h-full rounded-2xl">
                  <div className="h-full rounded-2xl bg-background/60 backdrop-blur p-5 sm:p-6 shadow-[var(--shadow-pop)] text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mx-auto mb-4">
                      <b.icon className="h-6 w-6" />
                    </div>
                    <div className="text-3xl sm:text-4xl font-bold text-white leading-none tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {b.num != null ? <CountUp end={b.num} suffix={b.suffix} /> : b.text}
                    </div>
                    <div className="text-xs font-medium uppercase tracking-wider text-white/60 mt-1.5">{b.unit}</div>
                    <div className="text-sm font-semibold uppercase tracking-wide text-primary mt-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{b.label}</div>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1.5">{b.desc}</p>
                  </div>
                </Tilt>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section id="features" className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">Features</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Everything you need for vendor compliance</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">From document intake to automated chasing, VendorSuite handles the full compliance lifecycle for property managers.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <Reveal key={i} delay={(i % 3) * 80} className="h-full">
                <Tilt max={6} glare={false} className="relative h-full rounded-xl">
                  <Card className="h-full rounded-xl border shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-pop)] transition-shadow duration-200">
                    <CardContent className="p-6">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                        <f.icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-semibold mb-2 text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{f.title}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
                    </CardContent>
                  </Card>
                </Tilt>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section id="how-it-works" className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <Badge variant="secondary" className="mb-4">How It Works</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Five steps to full compliance</h2>
            <p className="text-muted-foreground">From setup to automated management in minutes.</p>
          </div>
          <div className="space-y-6">
            {STEPS.map((step, i) => (
              <Reveal key={i} delay={i * 90}>
                <div className="flex gap-6 items-start">
                  <div className="flex flex-col items-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary text-primary-foreground font-bold text-lg shrink-0" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{step.num}</div>
                    {i < STEPS.length - 1 && <div className="w-px h-12 bg-border mt-2" />}
                  </div>
                  <div className="pb-4">
                    <h3 className="font-semibold text-lg mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{step.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── DASHBOARD PREVIEW ─── */}
      <section id="dashboard" className="py-20 px-6 bg-muted/30">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Dashboard</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Your compliance command center</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Real-time KPIs, compliance trends, vendor risk scores, and automated alerts — all in one view.</p>
          </div>
          {/* Stylized dashboard mockup */}
          <Tilt className="relative rounded-2xl overflow-hidden border border-border shadow-[var(--shadow-pop)] bg-card">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
              <div className="flex gap-1.5">
                <div className="h-3 w-3 rounded-full bg-destructive/60" />
                <div className="h-3 w-3 rounded-full bg-[hsl(36,90%,50%)]/60" />
                <div className="h-3 w-3 rounded-full bg-[hsl(142,56%,36%)]/60" />
              </div>
              <span className="text-xs text-muted-foreground ml-2">VendorSuite — Compliance Dashboard</span>
            </div>
            <div className="p-6">
              {/* KPI Row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                {[
                  { label: 'Total Vendors', value: '21', sub: '0 compliant', color: 'text-primary' },
                  { label: 'Expiring / Expired', value: '14', sub: '5 expired', color: 'text-destructive' },
                  { label: 'Docs Processing', value: '7', sub: '38 total', color: 'text-primary' },
                  { label: 'Upcoming Renewals', value: '0', sub: 'Next 60 days', color: 'text-[hsl(36,90%,50%)]' },
                ].map((kpi, i) => (
                  <div key={i} className="p-3 rounded-lg border border-border">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{kpi.label}</p>
                    <p className={`text-2xl font-bold tabular-nums ${kpi.color}`} style={{ fontFamily: 'Space Grotesk, sans-serif' }}><CountUp end={Number(kpi.value)} /></p>
                    <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
                  </div>
                ))}
              </div>
              {/* Chart + Score Row */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-4">
                <div className="sm:col-span-7 rounded-lg border border-border p-3 h-36">
                  <p className="text-xs font-medium mb-2">Compliance Trend</p>
                  <TrendBars />
                </div>
                <div className="sm:col-span-5 rounded-lg border border-border p-3 h-36 flex flex-col items-center justify-center">
                  <p className="text-xs font-medium mb-2">Portfolio Score</p>
                  <ScoreRing />
                  <div className="flex gap-2 mt-2">
                    <span className="text-[9px] flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-[hsl(36,90%,50%)]" />Warning (9)</span>
                    <span className="text-[9px] flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full bg-destructive" />Expired (5)</span>
                  </div>
                </div>
              </div>
              {/* Alerts + Vendors Row */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-5 rounded-lg border border-border p-3">
                  <div className="text-xs font-medium mb-2 flex items-center">Alerts <Badge variant="destructive" className="text-[9px] ml-1">2</Badge></div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-[10px] p-1.5 rounded bg-muted/50"><div className="h-1.5 w-1.5 rounded-full bg-destructive" /> COI expired for BrightSpark Electric</div>
                    <div className="flex items-center gap-2 text-[10px] p-1.5 rounded bg-muted/50"><div className="h-1.5 w-1.5 rounded-full bg-destructive" /> COI expired for Premier Plumbing</div>
                  </div>
                </div>
                <div className="sm:col-span-7 rounded-lg border border-border p-3">
                  <p className="text-xs font-medium mb-2">Top Risky Vendors</p>
                  <div className="space-y-1.5">
                    {['TopShield Roofing', 'CoolAir HVAC', 'PaintPro Contractors'].map((name, i) => (
                      <div key={i} className="flex items-center justify-between text-[10px] p-1.5 rounded bg-muted/50">
                        <span>{name}</span>
                        <Badge variant="destructive" className="text-[8px] h-4">Expired</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </Tilt>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            {[
              { label: 'Total Vendors', value: 'At a glance', icon: Users },
              { label: 'Compliance Trend', value: '12-month chart', icon: BarChart3 },
              { label: 'Portfolio Score', value: 'Real-time ring', icon: Shield },
              { label: 'Risk Alerts', value: 'Instant flags', icon: Sparkles },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-card border border-border">
                <item.icon className="h-5 w-5 text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium">{item.label}</p>
                  <p className="text-xs text-muted-foreground">{item.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Vendor Portal Preview */}
        <div className="max-w-3xl mx-auto mt-16">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4">Vendor Portal</Badge>
            <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>What your vendors see</h3>
            <p className="text-sm text-muted-foreground max-w-lg mx-auto">Vendors receive a magic link — no login required. Here's their zero-login compliance portal.</p>
          </div>
          <Tilt max={7} className="relative rounded-2xl overflow-hidden border border-border shadow-[var(--shadow-pop)] bg-card">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-muted/50 border-b border-border">
              <div className="flex gap-1.5">
                <div className="h-3 w-3 rounded-full bg-destructive/60" />
                <div className="h-3 w-3 rounded-full bg-[hsl(36,90%,50%)]/60" />
                <div className="h-3 w-3 rounded-full bg-[hsl(142,56%,36%)]/60" />
              </div>
              <span className="text-xs text-muted-foreground ml-2">VendorSuite — Vendor Portal</span>
            </div>
            <div className="p-4 sm:p-6 space-y-4">
              {/* Portal Header */}
              <div>
                <BrandLogoDark width={110} />
                <p className="text-[11px] text-muted-foreground mt-1.5">Compliance portal for <strong>Apex Property Management</strong></p>
              </div>

              {/* Vendor Status Card */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-semibold text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Premier Plumbing Co.</p>
                    <p className="text-[11px] text-muted-foreground">Plumber</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>85%</p>
                    <p className="text-[10px] text-muted-foreground">Compliance Score</p>
                  </div>
                </div>
                <div className="h-2 rounded-full bg-muted overflow-hidden">
                  <div className="h-full rounded-full bg-primary" style={{ width: '75%' }} />
                </div>
                <div className="flex items-center justify-between mt-2 text-[11px] text-muted-foreground">
                  <div className="flex gap-3">
                    <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-[hsl(142,56%,36%)]" /> 3 valid</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3 text-[hsl(36,90%,50%)]" /> 1 missing</span>
                  </div>
                  <Badge variant="secondary" className="text-[10px]">3 of 4 submitted</Badge>
                </div>
              </div>

              {/* Required Documents */}
              <div>
                <p className="text-xs font-medium mb-2.5" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Required Documents</p>
                <div className="space-y-2">
                  {[
                    { name: 'Certificate of Insurance', type: 'COI', valid: true, file: 'Premier_COI_2026.pdf', exp: '12/01/2026' },
                    { name: 'W-9 Tax Form', type: 'W9', valid: true, file: 'Premier_W9.pdf' },
                    { name: 'Trade License', type: 'LICENSE', valid: true, file: 'CA_License.pdf', exp: '06/15/2027' },
                    { name: 'Workers Compensation', type: 'WC', valid: false },
                  ].map((doc, i) => (
                    <div key={i} className={`flex items-center justify-between p-3 rounded-lg border ${doc.valid ? 'border-[hsl(142_40%_30%)] bg-[hsl(142_56%_8%)]' : 'border-[hsl(36_80%_30%)] bg-[hsl(36_100%_6%)]'}`}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className={`flex h-8 w-8 items-center justify-center rounded-lg shrink-0 ${doc.valid ? 'bg-[hsl(142_56%_18%)]' : 'bg-[hsl(36_100%_16%)]'}`}>
                          {doc.valid ? <CheckCircle2 className="h-4 w-4 text-[hsl(142,56%,50%)]" /> : <Clock className="h-4 w-4 text-[hsl(36,90%,55%)]" />}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-medium">{doc.name}</p>
                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[9px] h-4 px-1">{doc.type}</Badge>
                            {doc.file && <span className="text-[9px] text-muted-foreground truncate">{doc.file}</span>}
                            {doc.exp && <span className="text-[9px] text-muted-foreground">Exp: {doc.exp}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="shrink-0 ml-2">
                        {doc.valid ? (
                          <Badge variant="outline" className="text-[9px] text-[hsl(142,56%,50%)] border-[hsl(142,40%,30%)]">Valid</Badge>
                        ) : (
                          <div className="flex items-center gap-1 bg-primary text-primary-foreground text-[10px] font-medium px-2.5 py-1 rounded-md">
                            <Upload className="h-3 w-3" /> Upload
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Upload Zone */}
              <div className="border-2 border-dashed border-border rounded-xl p-4 text-center">
                <Upload className="mx-auto h-5 w-5 text-muted-foreground/40 mb-1" />
                <p className="text-[11px] text-muted-foreground">Drag & drop to upload additional documents</p>
                <p className="text-[9px] text-muted-foreground mt-0.5">PDF, PNG, JPEG, TIFF up to 25MB</p>
              </div>
            </div>
          </Tilt>
        </div>
      </section>

      {/* ─── TRY IT LIVE ─── */}
      <section id="try-it" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-10">
            <Badge variant="secondary" className="mb-4">Try It Live</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>See the AI actually work — right now</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">No sign-up, no video. Drop in a sample certificate (or your own) and watch VendorSuite extract every field and score compliance in seconds.</p>
          </Reveal>
          <Reveal delay={80}>
            <TryItDemo />
          </Reveal>
        </div>
      </section>

      {/* ─── PRICING ─── */}
      <section id="pricing" className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">Pricing</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Simple, transparent pricing</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Both plans include every feature. The only differences are property limits and billing flexibility.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
            {/* Professional */}
            <Reveal className="h-full">
            <Card className="h-full rounded-xl border shadow-[var(--shadow-soft)]">
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Professional</CardTitle>
                <CardDescription>For growing property management companies</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$249</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">+ $4.50 per active vendor/mo</p>
                </div>
                <Button className="w-full" variant="outline" onClick={() => navigate('/signup')}>Get Started <ChevronRight className="ml-1 h-4 w-4" /></Button>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2 text-sm"><Building2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong>Up to 5 properties</strong></span></div>
                  <div className="flex items-start gap-2 text-sm text-muted-foreground"><X className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" /><span>Vendor-Paid billing not available</span></div>
                  <div className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>All features included</span></div>
                </div>
              </CardContent>
            </Card>
            </Reveal>
            {/* Enterprise */}
            <Reveal delay={100} className="h-full">
            <Card className="h-full rounded-xl border border-primary shadow-[var(--shadow-pop)] relative">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">Best Value</Badge>
              <CardHeader className="pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Enterprise</CardTitle>
                <CardDescription>For large-scale property management operations</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold tabular-nums" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>$599</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">+ $3.50 per active vendor/mo</p>
                </div>
                <Button className="w-full" onClick={() => navigate('/signup')}>Get Started <ChevronRight className="ml-1 h-4 w-4" /></Button>
                <div className="space-y-2.5">
                  <div className="flex items-start gap-2 text-sm"><Infinity className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span><strong>Unlimited properties</strong></span></div>
                  <div className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-[hsl(142,56%,36%)] shrink-0 mt-0.5" /><span><strong>Vendor-Paid option</strong> — vendors pay $50/yr</span></div>
                  <div className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>Lower rate: $3.50/vendor/mo</span></div>
                  <div className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>All features included</span></div>
                </div>
              </CardContent>
            </Card>
            </Reveal>
          </div>
          {/* Shared features */}
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-sm font-medium mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Every feature, on both plans</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-2 text-left">
              {sharedFeatures.map((f, i) => (
                <div key={i} className="flex items-start gap-2 text-sm"><CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>{f}</span></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECURITY & COMPLIANCE ─── */}
      <section id="security" className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Security &amp; Compliance</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Built to protect your data</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">Enterprise-grade security is baked in, not bolted on. Here's exactly how we keep your portfolio and vendor documents safe.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SECURITY.map((s, i) => (
              <Reveal key={i} delay={(i % 4) * 70} className="h-full">
                <Tilt max={6} glare={false} className="relative h-full rounded-xl">
                  <div className="h-full rounded-xl border bg-card p-5 shadow-[var(--shadow-soft)]">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary mb-4">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <h3 className="font-semibold text-sm mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{s.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </Tilt>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FAQ ─── */}
      <section id="faq" className="py-20 px-6 bg-muted/30">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">FAQ</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {FAQS.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-border rounded-xl px-5 bg-card shadow-[var(--shadow-soft)]">
                <AccordionTrigger className="text-sm font-medium text-left py-4 hover:no-underline">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground leading-relaxed pb-4">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="text-center mt-8">
            <p className="text-sm text-muted-foreground mb-3">Still have questions?</p>
            <Button variant="outline" onClick={() => navigate('/signup')}>
              <HelpCircle className="mr-2 h-4 w-4" /> Contact Support
            </Button>
          </div>
        </div>
      </section>

      {/* ─── AFTER SIGNUP ─── */}
      <section className="py-20 px-6 bg-muted/30">
        <div className="max-w-5xl mx-auto">
          <Reveal className="text-center mb-14">
            <Badge variant="secondary" className="mb-4">Get Started in Minutes</Badge>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>What happens after you sign up</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">No onboarding calls, no setup fees. You'll be tracking compliance the same day.</p>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-3">
            {AFTER_SIGNUP.map((s, i) => (
              <Reveal key={i} delay={i * 90} className="h-full">
                <Tilt max={6} glare={false} className="relative h-full rounded-2xl">
                  <div className="h-full rounded-2xl border bg-card p-6 shadow-[var(--shadow-soft)]">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{i + 1}</div>
                      <s.icon className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{s.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                  </div>
                </Tilt>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── CTA ─── */}
      <section className="relative overflow-hidden py-20 px-6">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute top-1/2 left-1/2 h-[380px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/15 blur-[130px] landing-float-slow" />
        </div>
        <Reveal className="relative max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Start your free 30-day trial today</h2>
          <p className="text-muted-foreground mb-8">Full access to every feature. No credit card required, and you can cancel in one click.</p>
          <Button size="lg" className="transition-transform hover:scale-[1.03]" data-testid="cta-start-trial-button" onClick={() => navigate('/signup')}>
            Start Free 30-Day Trial <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {REASSURANCE.map((r) => (
              <span key={r} className="flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-primary" /> {r}</span>
            ))}
          </div>
        </Reveal>
      </section>

      <AppFooter />

      <style>{`
        @keyframes landingFloat {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(24px, -18px) scale(1.06); }
        }
        .landing-float { animation: landingFloat 14s ease-in-out infinite; }
        .landing-float-slow { animation: landingFloat 20s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .landing-float, .landing-float-slow { animation: none; }
        }
      `}</style>
    </div>
  );
}
